import { axiosInstance } from '@/config/axios.config';
import {
  Category,
  CreateCategoryRequest,
  CreateExerciseRequest,
  CreateObjectiveAttributeRequest,
  CreateTherapyAttributeRequest,
  CreateThemeRequest,
  Exercise,
  PaginatedCategoriesResponse,
  PaginatedExercisesResponse,
  PaginatedThemesResponse,
  ObjectiveAttribute,
  TherapyAttribute,
  Theme,
  UpdateCategoryRequest,
  UpdateExerciseRequest,
  UpdateObjectiveAttributeRequest,
  UpdateTherapyAttributeRequest,
  UpdateThemeRequest,
} from '@repo/types/src/glow-up';

// Theme Queries
export const getAllThemes = async (params?: {
  skip?: number;
  take?: number;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined)
    queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined)
    queryParams.append('take', params.take.toString());

  const url = `/glow-up/themes${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await axiosInstance.get<PaginatedThemesResponse>(url);
  return response.data;
};

export const getThemeById = async (id: number) => {
  const response = await axiosInstance.get<Theme>(`/glow-up/themes/${id}`);
  return response.data;
};

export const createTheme = async (payload: CreateThemeRequest) => {
  const response = await axiosInstance.post<Theme>('/glow-up/themes', payload);
  return response.data;
};

export const updateTheme = async (id: number, payload: UpdateThemeRequest) => {
  const response = await axiosInstance.patch<Theme>(
    `/glow-up/themes/${id}`,
    payload,
  );
  return response.data;
};

export const deleteTheme = async (id: number) => {
  const response = await axiosInstance.delete<Theme>(`/glow-up/themes/${id}`);
  return response.data;
};

// Category Queries
export const getAllCategories = async (params?: {
  skip?: number;
  take?: number;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined)
    queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined)
    queryParams.append('take', params.take.toString());

  const url = `/glow-up/categories${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await axiosInstance.get<PaginatedCategoriesResponse>(url);
  return response.data;
};

export const getCategoriesByTheme = async (themeId: number) => {
  const response = await axiosInstance.get<Category[]>(
    `/glow-up/categories?themeId=${themeId}`,
  );
  return response.data;
};

export const getCategoryById = async (id: number) => {
  const response = await axiosInstance.get<Category>(
    `/glow-up/categories/${id}`,
  );
  return response.data;
};

export const createCategory = async (payload: CreateCategoryRequest) => {
  const response = await axiosInstance.post<Category>(
    '/glow-up/categories',
    payload,
  );
  return response.data;
};

export const updateCategory = async (
  id: number,
  payload: UpdateCategoryRequest,
) => {
  const response = await axiosInstance.patch<Category>(
    `/glow-up/categories/${id}`,
    payload,
  );
  return response.data;
};

export const deleteCategory = async (id: number) => {
  const response = await axiosInstance.delete<Category>(
    `/glow-up/categories/${id}`,
  );
  return response.data;
};

// Exercise Queries
export const getAllExercises = async (params?: {
  skip?: number;
  take?: number;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.skip !== undefined)
    queryParams.append('skip', params.skip.toString());
  if (params?.take !== undefined)
    queryParams.append('take', params.take.toString());

  const url = `/glow-up/exercises${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await axiosInstance.get<PaginatedExercisesResponse>(url);
  return response.data;
};

export const getExercisesByCategory = async (categoryId: number) => {
  const response = await axiosInstance.get<Exercise[]>(
    `/glow-up/exercises?categoryId=${categoryId}`,
  );
  return response.data;
};

export const getExerciseById = async (id: number) => {
  const response = await axiosInstance.get<Exercise>(
    `/glow-up/exercises/${id}`,
  );
  return response.data;
};

export const createExercise = async (payload: CreateExerciseRequest) => {
  const response = await axiosInstance.post<Exercise>(
    '/glow-up/exercises',
    payload,
  );
  return response.data;
};

export const updateExercise = async (
  id: number,
  payload: UpdateExerciseRequest,
) => {
  const response = await axiosInstance.patch<Exercise>(
    `/glow-up/exercises/${id}`,
    payload,
  );
  return response.data;
};

export const deleteExercise = async (id: number) => {
  const response = await axiosInstance.delete<Exercise>(
    `/glow-up/exercises/${id}`,
  );
  return response.data;
};

// Objective Attribute Queries
export const getObjectiveAttributes = async () => {
  const response = await axiosInstance.get<ObjectiveAttribute[]>(
    '/glow-up/objective/objective-attributes',
  );
  return response.data;
};

export const createObjectiveAttribute = async (
  payload: CreateObjectiveAttributeRequest,
) => {
  const response = await axiosInstance.post<ObjectiveAttribute>(
    '/glow-up/objective/objective-attributes',
    payload,
  );
  return response.data;
};

export const updateObjectiveAttribute = async (
  id: number,
  payload: UpdateObjectiveAttributeRequest,
) => {
  const response = await axiosInstance.patch<ObjectiveAttribute>(
    `/glow-up/objective/objective-attributes/${id}`,
    payload,
  );
  return response.data;
};

export const deleteObjectiveAttribute = async (id: number) => {
  const response = await axiosInstance.delete<ObjectiveAttribute>(
    `/glow-up/objective/objective-attributes/${id}`,
  );
  return response.data;
};

// Therapy Attribute Queries
export const getTherapyAttributes = async () => {
  const response = await axiosInstance.get<TherapyAttribute[]>(
    '/glow-up/objective/therapy-attributes',
  );
  return response.data;
};

export const createTherapyAttribute = async (
  payload: CreateTherapyAttributeRequest,
) => {
  const response = await axiosInstance.post<TherapyAttribute>(
    '/glow-up/objective/therapy-attributes',
    payload,
  );
  return response.data;
};

export const updateTherapyAttribute = async (
  id: number,
  payload: UpdateTherapyAttributeRequest,
) => {
  const response = await axiosInstance.patch<TherapyAttribute>(
    `/glow-up/objective/therapy-attributes/${id}`,
    payload,
  );
  return response.data;
};

export const deleteTherapyAttribute = async (id: number) => {
  const response = await axiosInstance.delete<TherapyAttribute>(
    `/glow-up/objective/therapy-attributes/${id}`,
  );
  return response.data;
};
